import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { del, get, put } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { Dialog } from '@/components/ui/layout';
import { Badge, Button, Input, SkeletonText } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';

interface PermissionOverrideEntry {
  code: string;
  module: string;
  moduleLabel?: string;
  group: string;
  description: string;
  sensitive: boolean;
  grantedByRole: boolean;
  override: { effect: 'grant' | 'deny'; reason: string; createdAt: string } | null;
  effective: boolean;
}

export function PermissionOverridesDialog({ user, onClose }: { user: { id: string; name: string }; onClose: () => void }) {
  const entries = useQuery({
    queryKey: ['permission-overrides', user.id],
    queryFn: () => get<PermissionOverrideEntry[]>(`/org/users/${user.id}/permission-overrides`),
  });

  const grouped = new Map<string, PermissionOverrideEntry[]>();
  for (const entry of entries.data ?? []) {
    const key = `${entry.module}:${entry.group}`;
    grouped.set(key, [...(grouped.get(key) ?? []), entry]);
  }

  return (
    <Dialog
      open
      wide
      onClose={onClose}
      title={tr('admin', 'permissions_x', { name: user.name })}
      description={tr('admin', 'what_their_roles_grant_and_any_personal')}
      footer={
        <Button variant="ghost" onClick={onClose}>
          {tr('admin', 'close')}
        </Button>
      }
    >
      <div className="max-h-[28rem] space-y-4 overflow-y-auto">
        {entries.isLoading && <SkeletonText lines={2} />}
        {[...grouped.entries()].map(([key, group]) => (
          <div key={key}>
            <div className="mb-1 flex items-center gap-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{group[0].group}</p>
              <Badge variant="muted">{group[0].moduleLabel ?? group[0].module}</Badge>
            </div>
            <div className="space-y-1 rounded-md border p-2">
              {group.map((entry) => (
                <PermissionOverrideRow key={entry.code} userId={user.id} entry={entry} onChanged={() => entries.refetch()} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </Dialog>
  );
}

function PermissionOverrideRow({
  userId,
  entry,
  onChanged,
}: {
  userId: string;
  entry: PermissionOverrideEntry;
  onChanged: () => void;
}) {
  const toast = useToast();
  const [editing, setEditing] = useState<'grant' | 'deny' | null>(null);
  const [reason, setReason] = useState('');
  const state: 'inherit' | 'grant' | 'deny' = entry.override?.effect ?? 'inherit';

  const save = useMutation({
    mutationFn: (effect: 'grant' | 'deny') => put(`/org/users/${userId}/permission-overrides/${entry.code}`, { effect, reason }),
    onSuccess: () => {
      setEditing(null);
      setReason('');
      onChanged();
    },
    onError: (caught) => toast.error(describeError(caught)),
  });
  const clear = useMutation({
    mutationFn: () => del(`/org/users/${userId}/permission-overrides/${entry.code}`),
    onSuccess: onChanged,
    onError: (caught) => toast.error(describeError(caught)),
  });

  return (
    <div className="border-b py-1.5 last:border-b-0">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="text-sm">
          {entry.description}
          {entry.sensitive && (
            <Badge variant="warning" className="ms-1">
              {tr('admin', 'sensitive')}
            </Badge>
          )}
          {entry.grantedByRole && (
            <Badge variant="secondary" className="ms-1">
              {tr('admin', 'role')}
            </Badge>
          )}
          <span className="block font-mono text-[11px] text-muted-foreground">{entry.code}</span>
        </div>
        <div className="flex shrink-0 gap-1">
          <Button
            type="button"
            size="sm"
            variant={state === 'inherit' ? 'default' : 'outline'}
            onClick={() => {
              setEditing(null);
              if (state !== 'inherit') clear.mutate();
            }}
          >
            {tr('admin', 'inherit')}
          </Button>
          <Button type="button" size="sm" variant={state === 'grant' ? 'default' : 'outline'} onClick={() => setEditing('grant')}>
            {tr('admin', 'grant')}
          </Button>
          <Button type="button" size="sm" variant={state === 'deny' ? 'default' : 'outline'} onClick={() => setEditing('deny')}>
            {tr('admin', 'deny')}
          </Button>
        </div>
      </div>
      {entry.override && (
        <p className="mt-1 text-xs text-muted-foreground">
          {entry.override.effect === 'grant' ? tr('admin', 'granted') : tr('admin', 'denied')} — {entry.override.reason}
        </p>
      )}
      {editing && (
        <div className="mt-1 flex gap-2">
          <Input
            placeholder={tr('admin', 'reason_for_this_exception')}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <Button type="button" size="sm" disabled={reason.trim().length < 5 || save.isPending} onClick={() => save.mutate(editing)}>
            {tr('admin', 'save')}
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(null)}>
            {tr('admin', 'cancel')}
          </Button>
        </div>
      )}
    </div>
  );
}
